import React, { useEffect, useState, useCallback } from "react"
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Image,
  ActivityIndicator,
  Platform,
  Modal,
  RefreshControl,
  ScrollView,
} from "react-native"
import back from "./assets/back1.png"
import next from "./assets/next1.png"
import filter from "./assets/filter1.png"
import axios from "axios"
import AsyncStorage from "@react-native-async-storage/async-storage"
import { useFocusEffect } from "@react-navigation/native"
import API_PATHS from "../src/apiCalls/ApiPath"
import { isEventServiceType, eventTypeLabel } from "./screens/Common/helper"

export default function Bookings({ route, navigation }) {
  const [activeTab, setActiveTab] = useState("In-Progress")
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [filterVisible, setFilterVisible] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState(null)
  const [refreshing, setRefreshing] = useState(false)

  const selectedDate = route.params?.selectedDate

  const InProgressStatus = ["CREATED", "ACCEPTED", "ASSIGNED", "REJECTED", "ONGOING", "ENDED", "CANCELLED", "APPROVED"]
  const CompletedStatus = ["COMPLETED"]

  const typeDescriptionMap = {
    HOMECARE: "At Home Care",
    LEARNCARE: "At Learning Center",
    TRANSPORT: "Transportation",
    BIRTHDAY: "Birthday Party",
    VENREN: "Venue Rental",
    BREAKFASTCLUB: "Breakfast Club",
    SOCIALHUB: "Social Hub",
    "EVENT-STARTER": "Starter DIY Package",
    "EVENT-DELUX": "Deluxe Custom Packages",
    "EVENT-SIGNATURE": "Signature Curated Packages",
    "EVENT-PARTY_HELPER": "Party Helpers",
  }

  // Event bookings show a single start time (like the old Venue Rental flow).
  // Matches every event code format the backend sends (EVENT-*, EVENTS_*, ...).
  const isEventType = isEventServiceType

  const statusStyles = {
    ONGOING: {
      color: "#B7410E",
      backgroundColor: "#B7410E66",
      label: "on-going",
    },
    CREATED: { color: "#00BFFF", backgroundColor: "#00BFFF66", label: "Created" },
    COMPLETED: {
      color: "#006A4E",
      backgroundColor: "#006A4E66",
      label: "completed",
    },
    APPROVED: {
      color: "#654321",
      backgroundColor: "#65432166",
      label: "approved",
    },
    ACCEPTED: {
      color: "#D2B55B",
      backgroundColor: "#D2B55B66",
      label: "accepted",
    },
    ENDED: { color: "#000080", backgroundColor: "#00008066", label: "ended" },
    ASSIGNED: {
      color: "#769DB9",
      backgroundColor: "#769DB966",
      label: "assigned",
    },
    REJECTED: {
      color: "#4D4B46",
      backgroundColor: "#4D4B4666",
      label: "rejected",
    },
    CANCELLED: {
      color: '#C62828',
      backgroundColor: '#FFEBEE',
      label: 'cancelled'
    }
  }

  const filterOptions = [
    { label: "At Home Care", value: "HOMECARE" },
    { label: "Social Hub", value: "SOCIALHUB" },
    { label: "Transport", value: "TRANSPORT" },
    { label: "Breakfast Club", value: "BREAKFASTCLUB" },
    { label: "Starter DIY Package", value: "EVENT-STARTER" },
    { label: "Signature Curated Packages", value: "EVENT-SIGNATURE" },
    { label: "Deluxe Custom Packages", value: "EVENT-DELUX" },
    { label: "Party Helpers", value: "EVENT-PARTY_HELPER" },
  ]

  const getBookings = useCallback(
    async (status, pageNumber = 1, filter = selectedFilter) => {
      setLoading(true)
      try {
        const token = await AsyncStorage.getItem("token")
        if (!token) return

        const response = await axios.post(
          `${API_PATHS.BASE_URL}${API_PATHS.GET_BOOKINGS2}`,
          {
            page: pageNumber,
            limit: 10,
            status,
            fromDate: selectedDate,
            toDate: selectedDate,
            type: filter,
          },
          {
            headers: {
              "Content-Type": "application/json",
              authorization: `Bearer ${token}`,
            },
          },
        )

        if (response.data.Success === 1) {
          // 15 days buffer
          // const currentUnixTime = Math.floor(Date.now() / 1000) - (15 * 24 * 60 * 60)
          // .filter((booking) => booking.status == "ONGOING" || booking.status == "APPROVED" || booking.status == "COMPLETED" || booking.startTime > currentUnixTime)
          const newBookings = response.data.Data
            .map((booking, index) => ({
              ...booking,
              uniqueId: `${booking.id}-${Date.now()}-${index}`,
            }))
          setBookings((prevBookings) => (pageNumber === 1 ? newBookings : [...prevBookings, ...newBookings]))
          setHasMore(response.data.Data.length === 10)
          setPage(pageNumber)
        } else {
          setHasMore(false)
        }
      } catch (err) {
        console.error("Error fetching bookings:", err)
        setHasMore(false)
      } finally {
        setLoading(false)
      }
    },
    [selectedDate, selectedFilter],
  )

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    setPage(1)
    setHasMore(true)
    await getBookings(activeTab === "In-Progress" ? "INPROGRESS" : "COMPLETED", 1)
    setRefreshing(false)
  }, [activeTab, getBookings])

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      getBookings(activeTab === "In-Progress" ? "INPROGRESS" : "COMPLETED", page + 1)
    }
  }, [loading, hasMore, activeTab, page, getBookings])

  const handleFilterSelect = useCallback(
    (filterValue) => {
      setSelectedFilter((prevFilter) => (prevFilter === filterValue ? null : filterValue))
      setFilterVisible(false)
      setBookings([])
      setPage(1)
      setHasMore(true)
      getBookings(activeTab === "In-Progress" ? "INPROGRESS" : "COMPLETED", 1, filterValue)
    },
    [activeTab, getBookings],
  )

  useEffect(() => {
    getBookings(activeTab === "In-Progress" ? "INPROGRESS" : "COMPLETED", 1)
  }, [activeTab, getBookings])

  const renderFooter = () => {
    if (!loading) return null
    return <ActivityIndicator style={styles.loader} />
  }

  const renderBooking = ({ item }) => {
    const statusInfo = statusStyles[item.status]

    const timeConverter = (epochTime) => {
      const myDate = new Date(epochTime * 1000)
      const day = myDate.getDate().toString().padStart(2, "0")
      const year = myDate.getFullYear()
      const fullMonthName = myDate.toLocaleString("default", { month: "long" })
      const month = fullMonthName.length > 4 ? fullMonthName.slice(0, 3) : fullMonthName
      const formattedDate = `${day}-${month}-${year}`
      return formattedDate
    }

    function EpochTime(epochTime) {
      const date = new Date(epochTime * 1000)
      const hours = date.getHours()
      const minutes = date.getMinutes()
      const formattedHours = hours % 12 || 12
      const period = hours < 12 ? "AM" : "PM"
      const formattedMinutes = minutes.toString().padStart(2, "0")
      return `${formattedHours}:${formattedMinutes} ${period}`
    }

    const getTime = (startTime) => {
      const From = timeConverter(startTime)
      return From
    }

    return (
      <TouchableOpacity
        style={styles.bookingCard}
        onPress={() =>
          navigation.navigate("BookingDetails", {
            bookingId: item.bookingId,
          })
        }
      >
        <View style={styles.titleCard}>
          <Text style={styles.bookingTitle}>
            {typeDescriptionMap[item.type] || eventTypeLabel(item.type) || item.type}
          </Text>
          {item.type == "HOMECARE" || item.type == "LEARNCARE" || item.type == "SOCIALHUB" || item.type == "BREAKFASTCLUB" ? (
            <>
              <Text style={styles.bookingDate}>
                {getTime(item.startTime)} ,{EpochTime(item.startTime)}-{EpochTime(item.endTime)}
              </Text>
            </>
          ) : null}
          {item.type == "TRANSPORT" || item.type == "BIRTHDAY" || item.type == "VENREN" || isEventType(item.type) ? (
            <>
              <Text style={styles.bookingDate}>
                {getTime(item.startTime)}, {EpochTime(item.startTime)}
              </Text>
            </>
          ) : null}
          <Text style={{
            fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
            fontSize: 12,
            fontWeight: "700",
            backgroundColor: item.recurringId ? "#E1BEE7" : "#B3E5FC",
            color: item.recurringId ? "#7B1FA2" : "#0288D1",
            borderRadius: 20,
            paddingVertical: 4,
            paddingHorizontal: 12,
            width: 90,
            textAlign: "center",
            overflow: "hidden",
            marginLeft: "10%",
            marginTop: 5
          }}>
            {item.recurringId ? "Recurring" : "Adhoc"}
          </Text>
        </View>
        <View style={styles.statusContainer}>
          <View style={[styles.statusBadge, { backgroundColor: statusInfo.backgroundColor }]}>
            <Text style={[styles.statusText, { color: statusInfo.color }]}>{statusInfo.label}</Text>
          </View>
        </View>
        {/* <View>
          <Image source={next} style={styles.nextImage} resizeMode="contain" />
        </View> */}
      </TouchableOpacity>
    )
  }

  const FilterModal = () => (
    <Modal
      animationType="slide"
      transparent={true}
      visible={filterVisible}
      onRequestClose={() => setFilterVisible(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <Text style={styles.modalTitle}>Filter</Text>
          <Text style={styles.modalSubtitle}>Please select one of the options below</Text>

          <ScrollView style={styles.filterOptionsList}>
            {filterOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[styles.filterOption, selectedFilter === option.value && styles.selectedFilterOption]}
                onPress={() => handleFilterSelect(option.value)}
              >
                <Text
                  style={[styles.filterOptionText, selectedFilter === option.value && styles.selectedFilterOptionText]}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <TouchableOpacity
            style={styles.cancelButton}
            onPress={() => {
              setSelectedFilter(null)
              setFilterVisible(false)
              setBookings([])
              setPage(1)
              setHasMore(true)
              getBookings(activeTab === "In-Progress" ? "INPROGRESS" : "COMPLETED", 1, null)
            }}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  )

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        {selectedDate ? (
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Image style={styles.optionBackImage} source={back} />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity onPress={() => navigation.navigate("Recurring")}>
            <Image source={require("./assets/R.png")} style={styles.optionImage} />
          </TouchableOpacity>
        )}
        <Text style={styles.headerText}>{selectedDate ? `Bookings (${selectedDate})` : "Bookings"}</Text>
        <TouchableOpacity onPress={() => navigation.navigate("Notification")}>
          <Image source={require("./assets/notification1.png")} style={styles.notificationImage} />
        </TouchableOpacity>
      </View>

      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === "In-Progress" && styles.activeTab]}
          onPress={() => setActiveTab("In-Progress")}
        >
          <Text style={[styles.tabText, activeTab === "In-Progress" && styles.activeTabText]}>In-Progress</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === "Completed" && styles.activeTab]}
          onPress={() => setActiveTab("Completed")}
        >
          <Text style={[styles.tabText, activeTab === "Completed" && styles.activeTabText]}>Completed</Text>
        </TouchableOpacity>
      </View>

      <FilterModal />

      {loading && bookings.length === 0 ? (
        <ActivityIndicator
          size="large"
          color="#A6CE39"
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
          }}
        />
      ) : bookings.length > 0 ? (
        <FlatList
          data={bookings}
          renderItem={renderBooking}
          keyExtractor={(item) => item.uniqueId}
          contentContainerStyle={styles.listContainer}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.1}
          ListFooterComponent={renderFooter}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={["#A6CE39"]} />}
        />
      ) : (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>No bookings available</Text>
        </View>
      )}
      <TouchableOpacity style={styles.calendarButton} onPress={() => navigation.navigate("BookingCalendar")}>
        <Image source={require("./assets/calender1.png")} style={styles.calendarButton} />
      </TouchableOpacity>
      <TouchableOpacity style={styles.filterIcon} onPress={() => setFilterVisible(true)}>
        <Image source={filter} style={styles.filterIcon} />
      </TouchableOpacity>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
    position: "relative",
  },
  header: {
    backgroundColor: "#A6CE39",
    padding: 15,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  optionBackImage: {
    width: 15,
    height: 25,
  },
  optionImage: {
    width: 35,
    height: 35,
  },
  headerText: {
    color: "#fff",
    fontSize: 24,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    fontWeight: "bold",
    flex: 1,
    textAlign: "center",
  },
  notificationIconIcon: {
    width: 30,
    height: 30,
    position: "absolute",
    top: 15,
    right: 15,
  },
  tabContainer: {
    flexDirection: "row",
    justifyContent: "space-around",
    backgroundColor: "#fff",
    paddingVertical: 10,
    paddingHorizontal: 50,
  },
  tab: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 10,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#fff",
  },
  activeTab: {
    backgroundColor: "#A6CE39",
  },
  tabText: {
    fontSize: 18,
    color: "#979191",
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    fontWeight: "bold",
  },
  activeTabText: {
    color: "#fff",
  },
  listContainer: {
    padding: 10,
  },
  bookingCard: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: "#fff",
    padding: 15,
    borderRadius: 10,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
  },
  bookingTitle: {
    fontSize: 18,
    color: "#1A1A1A",
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    marginLeft: "10%",
  },
  bookingDate: {
    fontSize: 12,
    color: "#666",
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    marginLeft: "10%",
  },
  statusBadge: {
    width: 108,
    marginLeft: 26,
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 50,
  },
  statusText: {
    fontSize: 14,
    alignSelf: "center",
    textAlign: "center",
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    fontWeight: "bold",
  },
  titleCard: {
    width: "60%",
  },
  nextImage: {
    width: 10,
    height: 30,
  },
  notificationImage: {
    width: 28,
    height: 32,
  },
  calendarButton: {
    position: "absolute",
    bottom: 20,
    right: 10,
    width: 70,
    height: 70,
    zIndex: 1000,
  },
  filterIcon: {
    position: "absolute",
    bottom: 20,
    left: 10,
    width: 70,
    height: 70,
    zIndex: 1000,
  },
  emptyContainer: {
    padding: 15,
  },
  emptyText: {
    fontSize: 18,
    color: "#1A1A1A",
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
  },
  loader: {
    marginVertical: 10,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    width: "80%",
    maxHeight: "80%",
    backgroundColor: "white",
    borderRadius: 10,
    padding: 20,
    alignItems: "center",
  },
  filterOptionsList: {
    width: "100%",
    flexGrow: 0,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 10,
  },
  modalSubtitle: {
    fontSize: 16,
    color: "#666",
    marginBottom: 20,
    textAlign: "center",
  },
  filterOption: {
    width: "100%",
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  filterOptionText: {
    fontSize: 18,
    color: "#007AFF",
    textAlign: "center",
  },
  cancelButton: {
    marginTop: 10,
    padding: 15,
    width: "100%",
  },
  cancelButtonText: {
    fontSize: 18,
    color: "#007AFF",
    textAlign: "center",
  },

  selectedFilterOption: {
    backgroundColor: "#f0f0f0",
  },
  selectedFilterOptionText: {
    fontWeight: "bold",
  },
})

